'use client';

import { Open, Swap } from '@drift-labs/icons';
import { DepositExplanation, SpotBalanceType } from '@drift-labs/sdk';
import {
	matchEnum,
	UIMarket,
	UISerializableDepositRecord,
} from '@drift/common';
import { useEffect, useMemo, useState } from 'react';
import useDateDisplayState from 'src/hooks/useDateDisplayState';
import { HistoryTableUIProps } from 'src/hooks/historyData/historyDataTypes';
import { useDepositsHistory } from 'src/hooks/historyData/useDepositsHistory';
import useHistoryTableFilterStore from '../../stores/historyTableFilterStore/useHistoryTableFilterStore';
import { sortRecordsByTs } from 'src/utils/events';
import { getUrlForTx } from 'src/utils/explorer';
import TableV2 from '../Tables/TableV2';
import IconButton from 'src/components/primitives/IconButton/IconButton';
import Tag from 'src/components/primitives/Tag/Tag';
import Link from 'src/components/primitives/Link/Link';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';
import MobileTableCard from 'src/components/Tables/cards/MobileTableCard';
import MarketIcon from 'src/components/Utils/MarketIcon';
import Button from 'src/components/primitives/Button/Button';
import ToggleHeaderCell from 'src/components/Tables/cells/ToggleHeaderCell';
import NumberDisplay from 'src/components/NumberDisplay/NumberDisplay';
import HistoryTable from 'src/components/HistoryTables/HistoryTable';

const PAGE_SIZE = 20;
const EMPTY_DEPOSITS_ARRAY: UISerializableDepositRecord[] = [];
const TABLE_GRID = '1fr 1fr 1fr 1fr 1fr 50px';
const HEADERS = ['Time', 'Market', 'Amount', 'Notional', 'Type', ''];

const V2DepositWithdrawalHistoryTableDisplay = (
	props: HistoryTableUIProps<UISerializableDepositRecord>
) => {
	const filters = useHistoryTableFilterStore((s) =>
		s.getFilterState('deposits')
	);
	const currentUiPage = filters.currentUiPage;
	const { displayDatesAsTimestamp, toggleDateDisplayType } =
		useDateDisplayState();
	const isMobileScreenSize = useIsMobileScreenSize();

	const { historyData } = props;

	const fullDepositsWithdrawals: UISerializableDepositRecord[] =
		historyData.initialHistoryLoaded
			? historyData.records
			: EMPTY_DEPOSITS_ARRAY;
	const isLoading = historyData.loading;

	const filteredDepositsWithdrawals = useMemo(
		() =>
			props.filterFn
				? fullDepositsWithdrawals?.filter((record) =>
						props.filterFn(record, filters)
					)
				: fullDepositsWithdrawals,
		[fullDepositsWithdrawals, props.filterFn, filters]
	);

	const [currentDepositHistory, setCurrentDepositHistory] = useState(
		filteredDepositsWithdrawals?.slice(0, PAGE_SIZE) ?? undefined
	);

	const maxNumOfRecordsToDisplay =
		filters.getFilterMode() === 'SHORT_TERM'
			? historyData.totalCount // short term data API returns pages of 20 records up to the current page, and we do a workaround to (+1 to total count) when we know there is a next page
			: (filteredDepositsWithdrawals?.length ?? 0); // long term data API returns up to 5000 records for the first page, so we check the actual displayed records (filtered records) length

	// extract and set the current page records
	useEffect(() => {
		setCurrentDepositHistory(
			filteredDepositsWithdrawals?.slice(
				currentUiPage * PAGE_SIZE,
				(currentUiPage + 1) * PAGE_SIZE
			) ?? undefined
		);
	}, [filteredDepositsWithdrawals, currentUiPage]);

	return (
		<HistoryTable
			tableGrid={TABLE_GRID}
			pageSize={PAGE_SIZE}
			currentPage={currentUiPage}
			pageChangeCallback={props.executePageChange}
			totalItems={maxNumOfRecordsToDisplay}
			loading={isLoading}
			tableState={{
				records: filteredDepositsWithdrawals,
				id: 'deposit_history_table',
			}}
			tableContentTop={
				<TableV2.HeaderRow
					grid={TABLE_GRID}
					className="w-full"
					lastColumnJustify="start"
				>
					{HEADERS.map((label, index) => (
						<TableV2.HeaderCell
							key={`deposit_header_${index}`}
							className="gap-x-xs"
						>
							{label.toLowerCase() === 'time' ? (
								<ToggleHeaderCell
									key={`deposit_header_${index}`}
									label={label}
									onClick={toggleDateDisplayType}
								/>
							) : (
								label
							)}
						</TableV2.HeaderCell>
					))}
				</TableV2.HeaderRow>
			}
			tableContentMiddle={sortRecordsByTs(currentDepositHistory)?.map(
				(record, index) =>
					isMobileScreenSize ? (
						<MobileTableCard.Root
							key={`${index}_${record.ts}`}
							className="gap-y-lg"
						>
							<div className="flex items-center gap-x-xs">
								<MarketIcon
									marketSymbol={
										UIMarket.createSpotMarket(record.marketIndex).uiSymbols
											.marketDisplaySymbol
									}
									sizeClass="size-5"
								/>
								<span className="typo-t2">
									{
										UIMarket.createSpotMarket(record.marketIndex).uiSymbols
											.marketDisplaySymbol
									}
								</span>
							</div>
							<div className="grid w-full grid-cols-2">
								<MobileTableCard.LabelWithValue
									label="Amount"
									value={
										<NumberDisplay
											value={
												matchEnum(record.direction, SpotBalanceType.DEPOSIT)
													? record.amount
													: record.amount.neg()
											}
											type="number_signed"
											suffix={
												UIMarket.createSpotMarket(record.marketIndex).uiSymbols
													.marketDisplaySymbol
											}
											decimals={4}
											isAccountValueToHide
											signDependentColor
											trimZeroes
										/>
									}
								/>
								<MobileTableCard.LabelWithValue
									className="items-end"
									label="Status"
									value={
										record.transferUser
											? 'Transfer'
											: matchEnum(record.direction, SpotBalanceType.DEPOSIT)
												? 'Deposit'
												: 'Withdrawal'
									}
								/>
							</div>
							<div className="flex items-center gap-x-xs">
								<MobileTableCard.Label>Time</MobileTableCard.Label>
								<IconButton
									size="sm"
									variant="tertiary"
									className="-m-md p-md"
									onClick={toggleDateDisplayType}
									icon={Swap}
								/>
								<TableV2.DateCell
									className="w-fit py-0 whitespace-nowrap"
									displayAsTimestamp={displayDatesAsTimestamp}
									date={new Date(record.ts.toNumber() * 1000)}
								/>
							</div>
							<Button
								size="sm"
								variant="secondary"
								as="a"
								href={getUrlForTx(record.txSig)}
								openInNewTab
							>
								View in scanner
							</Button>
						</MobileTableCard.Root>
					) : (
						<TableV2.BodyRow
							key={`${index}_${record.ts}`}
							grid={TABLE_GRID}
							lastColumnJustify="start"
							className="h-10 border-0"
							isDataRow
						>
							<TableV2.DateCell
								key={`size_${index}`}
								date={new Date(record.ts.toNumber() * 1000)}
								displayAsTimestamp={displayDatesAsTimestamp}
							/>

							<TableV2.MarketCell
								key={`market_${index}`}
								marketSymbol={
									UIMarket.createSpotMarket(record.marketIndex).uiSymbols
										.marketDisplaySymbol
								}
								className="items-center"
							/>

							<TableV2.AssetCell
								key={`base_amount_${index}`}
								value={
									matchEnum(record.direction, SpotBalanceType.DEPOSIT)
										? record.amount
										: record.amount.neg()
								}
								suffix={
									UIMarket.createSpotMarket(record.marketIndex).uiSymbols
										.marketDisplaySymbol
								}
								decimals={4}
								disableTooltip
								isAccountValueToHide
								trimZeroes
								semantic
							/>

							<TableV2.NotionalCell
								key={`quote_amount_${index}`}
								value={
									matchEnum(record.direction, SpotBalanceType.DEPOSIT)
										? record.amount.mul(record.oraclePrice)
										: record.amount.mul(record.oraclePrice).neg()
								}
								isAccountValueToHide
							/>

							<TableV2.BodyCell
								key={`direction_${index}`}
								innerClassName="gap-x-xs"
							>
								{record.transferUser
									? 'Transfer'
									: matchEnum(record.direction, SpotBalanceType.DEPOSIT)
										? 'Deposit'
										: 'Withdrawal'}
								{matchEnum(record.explanation, DepositExplanation.BORROW) ? (
									<Tag className="bg-button-secondary-bg-hover text-text-default">
										Borrow
									</Tag>
								) : matchEnum(
										record.explanation,
										DepositExplanation.REPAY_BORROW
								  ) ? (
									<Tag className="bg-button-secondary-bg-hover text-text-default">
										Repay Borrow
									</Tag>
								) : null}
							</TableV2.BodyCell>

							<TableV2.BodyCell className="justify-end">
								<Link
									variant="secondary"
									href={getUrlForTx(record.txSig)}
									rightIcon="none"
									openInNewTab
								>
									<Open size={16} />
								</Link>
							</TableV2.BodyCell>
						</TableV2.BodyRow>
					)
			)}
		/>
	);
};

const V2DepositWithdrawalHistoryTable = () => {
	const tableState = useDepositsHistory();

	return <V2DepositWithdrawalHistoryTableDisplay {...tableState} />;
};

export default V2DepositWithdrawalHistoryTable;
